import type { ItemCollection } from '@toolproof-core/visualization/_contract/collection';
import type {
    ConnectorItemCollectionDerivation,
    ConnectorItemDerivation,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import {
    isMacroStepConnectorsEnabled,
} from '@/workflow/_pure/config/derivationConfig';
import { buildMacroStepConnectorCollectionKey, parseToolStepCollectionKey, type WorkflowKeyScope } from '@/workflow/_pure/keyCodec';
import {
    buildMacroStepConnectorItemCollectionDerivations,
} from '@/workflow/_pure/derivation/collections';
import { toMacroStepConnectorItemDerivation } from '@/workflow/_pure/derivation/itemFactories';
import { deriveResourceTypeAngularInfo, resolveMacroStepHelperLaneAngles } from '@/workflow/_pure/derivation/resourceTypeAngularPlacement';
import type { BaseConfig } from '@/workflow/_contract/config';
import type { MacroStepConnectorSeed } from '@/workflow/_contract/kernel';

export function deriveMacroStepConnectorCollectionDerivationsFromSeeds(args: {
    itemCollections: ItemCollection[];
    config: BaseConfig;
    seeds: readonly MacroStepConnectorSeed[];
    workflowKeyScope: WorkflowKeyScope;
}): ConnectorItemCollectionDerivation[] {
    if (!isMacroStepConnectorsEnabled(args.config)) return [];
    if (args.seeds.length === 0) return [];

    const toolStepCollection = args.itemCollections.find(
        (itemCollection): itemCollection is ItemCollection<'entity'> =>
            itemCollection.itemCollectionKind === 'entity'
            && parseToolStepCollectionKey(itemCollection.itemCollectionKey) !== null
    );
    if (!toolStepCollection || toolStepCollection.items.length === 0) return [];

    const resourceTypeInfo = deriveResourceTypeAngularInfo({
        itemCollections: args.itemCollections,
        config: args.config,
    });
    const laneAngles = resolveMacroStepHelperLaneAngles({ resourceTypeInfo, config: args.config });

    const itemDerivationsByCollectionKey = new Map<ItemCollection<'connector'>['itemCollectionKey'], ConnectorItemDerivation[]>();
    for (const seed of args.seeds) {
        const startToolStepItem = toolStepCollection.items[seed.startStepIndex];
        const endToolStepItem = toolStepCollection.items[seed.endStepIndex];
        if (!startToolStepItem || !endToolStepItem) {
            continue;
        }

        const itemDerivation = toMacroStepConnectorItemDerivation({
            logicalMacroStepKey: seed.logicalMacroStepKey,
            startToolStepKey: seed.startToolStepKey,
            endToolStepKey: seed.endToolStepKey,
            startToolStepItemKey: startToolStepItem.itemKey,
            endToolStepItemKey: endToolStepItem.itemKey,
            laneAngle: seed.macroStepKind === 'branch' ? laneAngles.branchAngle : laneAngles.loopAngle,
            macroStepKind: seed.macroStepKind,
            startStepIndex: seed.startStepIndex,
            endStepIndex: seed.endStepIndex,
            config: args.config,
            endRole: seed.endRole,
            workflowKeyScope: args.workflowKeyScope,
        });

        const collectionKey = buildMacroStepConnectorCollectionKey(args.workflowKeyScope);
        const current = itemDerivationsByCollectionKey.get(collectionKey) ?? [];
        current.push(itemDerivation);
        itemDerivationsByCollectionKey.set(collectionKey, current);
    }

    return buildMacroStepConnectorItemCollectionDerivations({
        itemDerivationsByCollectionKey,
    });
}